import { Injectable } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { RuntimeConfigService } from './runtime-config.service';

/**
 * Opens Scholar parent-portal links in the browser surface whose cookie jar holds the shared
 * Okta session, so the silentSso round-trip actually signs the user in (see
 * RuntimeConfigService.portalUrl).
 *
 * Under Capacitor, window.open/_blank launches the EXTERNAL system browser, which on iOS can
 * never see that session: interactive login runs in an SFSafariViewController sheet
 * (AppDelegate.PermanentWebViewDelegate), and since iOS 11 SFSafariViewController website data
 * is scoped to the app — the Safari app never receives the Okta session cookie, so prompt=none
 * comes back login_required and the parent is asked to sign in again. The Capacitor Browser
 * plugin instead opens SFSafariViewController on iOS (same app-scoped store as the login sheet)
 * and a Custom Tab on Android (same cookie jar as the login Custom Tab), so both platforms
 * arrive already signed in. On web the plugin degrades to plain window.open(url, '_blank'),
 * which is the behavior these links always had there.
 */
@Injectable({ providedIn: 'root' })
export class PortalLinkService {
  constructor(private runtimeConfig: RuntimeConfigService) {}

  /** Open the portal page at `path` (URL built by RuntimeConfigService.portalUrl). */
  open(path: string): void {
    this.openUrl(this.runtimeConfig.portalUrl(path));
  }

  /**
   * Click hook for template anchors whose [href] is already a portal URL: on native, cancel
   * the anchor navigation (which would go to the external browser) and open in the in-app
   * browser instead; on web, do nothing so the anchor's normal target=_blank behavior runs
   * (keeping middle-click, copy-link, etc. intact).
   */
  interceptAnchorClick(event: Event, url: string | null): void {
    if (!Capacitor.isNativePlatform() || !url) return;
    event.preventDefault();
    this.openUrl(url);
  }

  private openUrl(url: string): void {
    // If the native in-app browser can't open, fall back to the external browser rather than
    // a dead tap (on web the plugin is itself window.open, so the catch never fires).
    Browser.open({ url }).catch(() => window.open(url, '_blank'));
  }
}
